import { isReservedBrowserMcpEntry } from "./browser-mcp-availability.js";
/**
 * 企业 admin 判定单一真相源（2026-07 org-admin portal 提升到 shared）。
 *
 * 消费方：
 *  - dosia-enterprise (apps/web/server/enterprise/config.ts re-export)：
 *    buildEnterpriseBootstrap 的 `isAdmin` + 员工 JWT roleTemplateId gate
 *  - admin-portal server：POST /api/admin/org-login 的 org-admin 资格判定 +
 *    orgs-roles 高危字段闸
 *  - admin-portal-web：角色编辑器高危字段只读态渲染
 */
/** Role templateId that grants admin permissions. Matches `buildEnterpriseBootstrap`
 *  which sets `isAdmin = roleConfig.templateId === ADMIN_ROLE_TEMPLATE_ID`. */
export const ADMIN_ROLE_TEMPLATE_ID = "admin";
/** Admin gate — single-source refactor 2026-06. Identity is keyed on the
 *  immutable `role.code`, but admin permission is a property of the **capability
 *  template**, not the code or the customer-editable display name. So gates test
 *  the role's `templateId === "admin"`. JWT carries `roleTemplateId` explicitly
 *  (code ≠ templateId in general). Renaming the admin role's display name no
 *  longer affects the gate. */
export function isEnterpriseAdminTemplate(templateId) {
    return templateId === ADMIN_ROLE_TEMPLATE_ID;
}
/**
 * `RoleWorkspaceConfig` 顶层字段中，org-admin（客户企业管理员）在 admin-portal
 * 只读、仅平台超管可改的高危 key（2026-07-03 用户拍板终态：DOSIA 全局 /
 * agent 层体验把控留平台，组织自治项下放）。
 *
 * 入选标准 = 员工机器上的执行安全边界或权限模板体系（DOSIA 交付质量承诺）：
 *  - templateId        权限模板绑定（改成 "admin" 即提权，身份/能力单源）
 *  - sandbox           SDK 命令沙盒策略
 *  - hooks             hook 规则（任意命令注入面）
 *  - managedSettings   例外见 ORG_ADMIN_EDITABLE_MANAGED_SETTINGS_KEYS 子字段白名单
 *  - disallowedTools   工具禁用清单（agent 层体验/安全边界）
 *
 * agentRoleBindings（内置 Agent 可见性）2026-07-03 下放企业侧 —— 客户决定
 * 自己组织哪个角色能用哪个内置 Agent 是经营决策。
 *
 * server 端闸（orgs-roles）与前端只读态共用此清单，禁各写一份。
 */
export const ORG_ADMIN_READONLY_ROLE_DOSIA_KEYS = [
    "templateId",
    "sandbox",
    "hooks",
    "managedSettings",
    "disallowedTools",
];
/**
 * managedSettings 内部 org-admin 可编辑的子字段白名单（2026-07-03 下放）。
 * 判据：客户组织的成本与模型治理 = 经营决策，不触员工机器执行安全。
 * 白名单以外的一切子 key（permissions/env/escape-hatch 未知键…）默认锁死。
 *
 * 消费方：orgs-roles server 闸（managedSettings 子字段级比较）+
 * admin-portal-web PolicyEditor「预算与模型策略」结构化区。
 */
export const ORG_ADMIN_EDITABLE_MANAGED_SETTINGS_KEYS = [
    "maxAllowedBudgetUsd",
    "maxAllowedEffort",
    "visibleGroups",
    "lockRoleMapping",
    // L2 模型治理黑名单 disabledModels 已退役（2026-07-17 enforcement 换血）：从本白名单
    // 移除即等效「拒写」——server 闸（orgs-roles）与 PolicyEditor 共用本清单，移除后
    // managedSettings.disabledModels 变成隐式锁死子字段（同 sandbox/hooks 姿态），org-admin
    // 无论走结构化字段还是 managedSettingsJson 裸编辑都写不进去。模型治理改走三层漏斗
    // dosia.modelSelection（本文件外，独立顶层字段，PolicyEditor「模型圈定」区）。
    // 组织记忆开关（2026-07-10）：记忆是否对本角色员工启用 = 数据合规/经营决策，下放
    // org-admin。role 级经此闸；org 级默认另存（server 构建时收严合并，red line #9）。
    "orgMemoryEnabled",
    // 员工「记忆读取」自关开关的授予（2026-07-10）：是否给员工自己开/关记忆读取的能力 =
    // 同一组数据合规/经营决策，随 orgMemoryEnabled 同批下放。缺省=给；显式 false=收走
    // （员工端只剩统计+只读浏览，见 MemoryTab / shared types memoryReadToggleAllowed）。
    "memoryReadToggleAllowed",
    // OPT-2 权限层（2026-07-11）：CLI 原生规则引擎的 allow/ask/deny/defaultMode/
    // disableBypassPermissionsMode 下发 = 该角色员工机器上「哪些工具/命令自动放行·
    // 询问·禁止」的经营与安全边界配置，org-admin 自治。内容形态由唯一构建器
    // buildManagedPermissions 把关（本文件下方）；危险命令 74 条黑名单地板不在此配、
    // 由客户端 permission-resolver 恒生效兜底（admin 配任意规则也拦不穿地板）。
    "permissions",
    // 云端任务日预算阈值（⑥，2026-07-10；2026-07-15 起只告警不拦）：该角色云端任务每北京日模型花费告警线 = 成本经营
    // 决策，下放 org-admin。role 级经此闸；org 级默认另存（server 构建时收严取小，red line #9）。
    "cloudTaskDailyBudgetUsd",
    // 开放连接自助接入（OPT-6，2026-07-11）：逐档连接器开关（connectors）+ agent 自助接入
    // 模式（agentConnectorMode）= 该角色员工能自建哪类连接器、agent 能否全自动接入的经营与
    // 安全边界，org-admin 自治。形态由唯一构建器 buildConnectorTiers 把关（本文件下方）；
    // 任意命令型的「风险横幅+确认卡」地板在客户端恒生效，admin 关不掉也绕不过。
    "connectors",
    "agentConnectorMode",
];
/**
 * 内置 Agent 角色绑定单源事实（id / 展示名 / 产品默认可见角色）。
 * 完整种子（systemPrompt/tools/skills…）在 packages/core builtin-agents.ts，
 * 其 name/roles 字段从这里取值（红线 #9：admin-portal-web 的 Agent 可见性
 * 矩阵与 core 种子共用一份，禁两处手搓漂移）。
 *
 * defaultRoles = [] 表示产品默认全员可见。
 */
export const BUILTIN_AGENT_ROLE_DEFAULTS = [
    // 5 个业务 Agent（策划/换图/会议纪要/竞品拆解/浏览器批量）已于 2026-07-14 退役
    // 出系统内置、迁去组织 org 池（origin='org'，管理员发布、服务端下发）。org 池
    // agent 的可见角色由 AgentManifestV1.roles[] / admin agentRoleBindings 承载，
    // 不再进本表。仅真·系统级 Agent 留此。见 core builtin-agents.ts。
    // 创建向导（2026-07-13 任务入口 spec §2 + PR4 统一「创建向导」）：全员可见——建
    // 定时任务 / 建 Agent 助手都不是管理动作，是每个人自己的私事（各自排各自的 9 点
    // 日报、建各自的助手）。id 保持 builtin-agent-create-task（持久化 seed 键，不改值）。
    { id: "builtin-agent-create-task", name: "创建向导", defaultRoles: [] },
    // 浏览器助手（可选的浏览器专用 workflow，红线 #1 三源对撞见 PR body）：全员可见——
    // 任何人都可能临时需要操作网页；它是便捷的专用入口，不是管理动作也不是唯一入口。
    { id: "builtin-agent-browser", name: "浏览器助手", defaultRoles: [] },
];
/**
 * 内置「提交审核 Agent」的稳定 id（Lean Agent 档载体1）。
 *
 * 刻意 **不** 进 BUILTIN_AGENT_ROLE_DEFAULTS —— approver 是动态身份（企业 admin /
 * 部门负责人 getApprovableScopes），不是角色矩阵成员。可见性由 renderer 的
 * `isBuiltinReviewAgentVisible` 谓词单源把控（矩阵 roles:[] 放行、真闸在 approver
 * 过滤），故种子里 roles=[] 但工作台/设置两面仍只对 admin/leader 显示。
 */
export const BUILTIN_REVIEW_AGENT_ID = "builtin-agent-review";
/**
 * 内置「创建向导」的稳定 id（Lean Agent 档载体1 · 统一创建入口：建任务 + 建 Agent）。
 * 与 review agent 不同：这个**进** BUILTIN_AGENT_ROLE_DEFAULTS（defaultRoles:[] 全员
 * 可见）——创建无身份闸，谁都能建自己的排期任务 / Agent 助手。symbol 名保留
 * CREATE_TASK（内部基建名，红线 #9 只清退用户可见面），字符串值是持久化 seed 键，不改。
 */
export const BUILTIN_CREATE_TASK_AGENT_ID = "builtin-agent-create-task";
/**
 * 内置「浏览器助手」的稳定 id（可选的浏览器专用 workflow，2026-07-19）。
 *
 * 实测背景：ToolSearch 会延后 chrome-devtools 的 28 个工具定义，订阅与未订阅之间的
 * 首轮差值约 669 token（~0.7K），不是曾误记的 ~8.5K。浏览器助手仍提供预配置的专用
 * 浏览器会话（capabilities.mcpServers 固定含 "chrome-devtools"），但不再是唯一入口：
 * 裸对话默认未订阅，员工可在单个对话自行开启浏览器 MCP（仍受安装状态与管理员全局禁用约束）。
 *
 * 进 BUILTIN_AGENT_ROLE_DEFAULTS（defaultRoles:[] 全员可见）——转交无身份闸。
 */
export const BUILTIN_AGENT_BROWSER_ID = "builtin-agent-browser";
// ─────────────────────────────────────────────────────────────────────────
// OPT-2 权限层 · ManagedSettings.permissions 唯一 typed builder/validator（红线 #9）
// ─────────────────────────────────────────────────────────────────────────
/**
 * SDK `SDKPermissionMode` 合法值（claude-agent-sdk.d.ts:144，与 SDK 逐字对齐）。
 * defaultMode 只能取这些；单一真相源，PolicyEditor 下拉与本构建器共用。
 */
export const MANAGED_PERMISSION_MODES = [
    "default",
    "acceptEdits",
    "bypassPermissions",
    "plan",
    "dontAsk",
    "auto",
];
const PERMISSION_RULE_LABELS = {
    allow: "允许",
    ask: "询问",
    deny: "禁止",
};
/**
 * 校验单条权限规则串形态：`ToolName` 或 `ToolName(rule)`。
 *  - 工具名非空、不含空白与括号（Bash / Write / WebSearch / mcp__x__y 皆可）；
 *  - 带括号时必须 `)` 结尾、括号内非空（Bash(git push:*) / Read(//path)）。
 * **不做内容级拦截**（admin 可配任意规则；74 条危险命令黑名单地板在客户端
 * permission-resolver 恒生效兜底，任何规则都拦不穿地板）。
 */
export function isValidPermissionRule(rule) {
    const s = rule.trim();
    if (!s)
        return false;
    const open = s.indexOf("(");
    if (open === -1)
        return /^[^\s()]+$/.test(s);
    if (!s.endsWith(")"))
        return false;
    const tool = s.slice(0, open);
    const inner = s.slice(open + 1, -1);
    if (!/^[^\s()]+$/.test(tool))
        return false;
    return inner.trim().length > 0;
}
function normalizePermissionRuleArray(raw, field) {
    if (raw === undefined || raw === null)
        return [];
    if (!Array.isArray(raw)) {
        throw new Error(`权限「${PERMISSION_RULE_LABELS[field]}」必须是字符串数组`);
    }
    const out = [];
    const seen = new Set();
    for (const el of raw) {
        if (typeof el !== "string") {
            throw new Error(`权限「${PERMISSION_RULE_LABELS[field]}」含非字符串项`);
        }
        const s = el.trim();
        if (!s)
            continue;
        if (!isValidPermissionRule(s)) {
            throw new Error(`权限「${PERMISSION_RULE_LABELS[field]}」规则非法：「${el}」（应为 工具名 或 工具名(规则) 形态）`);
        }
        if (seen.has(s))
            continue;
        seen.add(s);
        out.push(s);
    }
    return out;
}
function normalizeAdditionalDirectories(raw) {
    if (raw === undefined || raw === null)
        return [];
    if (!Array.isArray(raw)) {
        throw new Error("additionalDirectories 必须是字符串数组");
    }
    const out = [];
    const seen = new Set();
    for (const el of raw) {
        if (typeof el !== "string") {
            throw new Error("additionalDirectories 含非字符串项");
        }
        const s = el.trim();
        if (!s || seen.has(s))
            continue;
        seen.add(s);
        out.push(s);
    }
    return out;
}
/**
 * `ManagedSettings.permissions` 的**唯一 typed builder/validator**（红线 #9）。
 *
 * admin-portal PolicyEditor serialize 前必过此校验；服务端白名单
 * （ORG_ADMIN_EDITABLE_MANAGED_SETTINGS_KEYS 含 "permissions"）放行该子字段后，
 * 形态由本构建器统一把关，禁多处手搓 permissions object literal。
 *
 * 校验：allow/ask/deny 每项为合法规则串（`Tool` / `Tool(rule)`）、trim、去空、各数组
 * 内去重；defaultMode ∈ MANAGED_PERMISSION_MODES；disableBypassPermissionsMode 只允许
 * "disable"；additionalDirectories 为字符串数组（去空去重）。
 *
 * **不做内容级拦截**：admin 可配任意规则（含试图 allow 危险命令），地板由客户端
 * permission-resolver 恒生效兜底。校验失败 throw Error（消息=面向 admin 的中文提示，
 * PolicyEditor 捕获后 inline amber 显示）。
 *
 * 全字段皆空 → 返回 undefined（不落 permissions 键，与 buildEmployeeManagedSettings
 * 空即省略同姿态）。入参不改。
 */
export function buildManagedPermissions(input) {
    const allow = normalizePermissionRuleArray(input.allow, "allow");
    const ask = normalizePermissionRuleArray(input.ask, "ask");
    const deny = normalizePermissionRuleArray(input.deny, "deny");
    const additionalDirectories = normalizeAdditionalDirectories(input.additionalDirectories);
    let defaultMode;
    if (input.defaultMode !== undefined &&
        input.defaultMode !== null &&
        input.defaultMode !== "") {
        if (typeof input.defaultMode !== "string" ||
            !MANAGED_PERMISSION_MODES.includes(input.defaultMode)) {
            throw new Error(`defaultMode 非法：「${String(input.defaultMode)}」（合法值：${MANAGED_PERMISSION_MODES.join(" / ")}）`);
        }
        defaultMode = input.defaultMode;
    }
    let disableBypass;
    if (input.disableBypassPermissionsMode !== undefined &&
        input.disableBypassPermissionsMode !== null) {
        if (input.disableBypassPermissionsMode !== "disable") {
            throw new Error('disableBypassPermissionsMode 只能是 "disable" 或不设置');
        }
        disableBypass = "disable";
    }
    const out = {};
    if (allow.length > 0)
        out.allow = allow;
    if (ask.length > 0)
        out.ask = ask;
    if (deny.length > 0)
        out.deny = deny;
    if (additionalDirectories.length > 0) {
        out.additionalDirectories = additionalDirectories;
    }
    if (defaultMode)
        out.defaultMode = defaultMode;
    if (disableBypass)
        out.disableBypassPermissionsMode = disableBypass;
    return Object.keys(out).length > 0 ? out : undefined;
}
// ─────────────────────────────────────────────────────────────────────────
// OPT-6 开放连接自助接入 · 连接器档位/模式 唯一 typed builder + resolver（红线 #9）
// ─────────────────────────────────────────────────────────────────────────
/** 三档连接器 key，单一真相源；PolicyEditor 卡片、个人设置面、propose_connector 闸共用。 */
export const CONNECTOR_TIERS = ["remote", "npm", "command"];
/** agent 自助接入模式合法值，单一真相源（PolicyEditor 下拉 + 个人面 + tool 闸共用）。
 *  严格程度递增：auto(0) < confirm(1) < off(2)。 */
export const AGENT_CONNECTOR_MODES = ["auto", "confirm", "off"];
const AGENT_CONNECTOR_MODE_SEVERITY = {
    auto: 0,
    confirm: 1,
    off: 2,
};
/**
 * `ConnectorTiers` 的**唯一 typed builder**（红线 #9）——「空即省略」姿态：只把**显式
 * 关闭**（false）的档写进结果，未设/开启的档不落键（undefined = 开）。全档皆开 → 返回
 * undefined（不落 connectors 键，与 buildManagedPermissions 同姿态）。
 *
 * 供 admin-portal PolicyEditor serialize（ManagedSettings.connectors）与 desktop 个人设置
 * （AppSettings.connectorTiers）共用，禁多处手搓 object literal。入参宽松（UI 三个布尔），
 * 入参不改。
 */
export function buildConnectorTiers(input) {
    const out = {};
    for (const tier of CONNECTOR_TIERS) {
        if (input[tier] === false)
            out[tier] = false;
    }
    return Object.keys(out).length > 0 ? out : undefined;
}
/**
 * 生效连接器档位 = 个人面 ∩ 企业面（policy 层「只收不放」）——任一为 false → 该档关闭；
 * 缺省（undefined）视为开。**单一真相源**：UI 隐藏表单 tab 与 propose_connector 档位闸
 * 消费同一函数，禁各写一份 ∩ 逻辑。
 */
export function resolveConnectorTiers(personal, managed) {
    const out = {};
    for (const tier of CONNECTOR_TIERS) {
        out[tier] = personal?.[tier] !== false && managed?.[tier] !== false;
    }
    return out;
}
/**
 * 生效 agent 自助接入模式 = 个人面与企业面**取更严者**（off > confirm > auto）。缺省
 * （undefined）视为 auto。**单一真相源**，propose_connector 闸消费。任意命令型的强制
 * confirm 地板不在此函数（由 tool 闸对 command 档恒加，见 propose-connector-tool）。
 */
export function resolveAgentConnectorMode(personal, managed) {
    const p = personal && AGENT_CONNECTOR_MODES.includes(personal) ? personal : "auto";
    const m = managed && AGENT_CONNECTOR_MODES.includes(managed) ? managed : "auto";
    return AGENT_CONNECTOR_MODE_SEVERITY[p] >= AGENT_CONNECTOR_MODE_SEVERITY[m] ? p : m;
}
/** 合法 MCP server 名：非空、无空白、无 `/`（用作 SDK tool 前缀 `mcp__<name>__<tool>`）。 */
function isValidConnectorName(name) {
    return /^[^\s/]+$/.test(name);
}
/** 面向模型 / 中文兜底的拒绝原因；设置表单按 code 显示 `settings.mcp.reservedBrowserConnector`。 */
const RESERVED_BROWSER_CONNECTOR_ERROR = "浏览器是 DOSIA 自带的能力，不能再接一个同名或跑 chrome-devtools-mcp 的连接器。" +
    "要让 AI 操作网页，请点输入框下方的「浏览器」按钮，为这个对话打开 DOSIA 的浏览器。";
function reservedBrowser() {
    return { ok: false, error: RESERVED_BROWSER_CONNECTOR_ERROR, code: "reserved-browser" };
}
/**
 * 三档连接器提案 → 唯一 typed `McpServerEntry` 装配器 + 校验器（红线 #9）。
 * **单一真相源**：设置面三档表单落 workspace.mcpServers 与 agent propose_connector 落库
 * 共用本函数，禁两处各拼一份 entry（npm→npx 组装、remote→http/sse 映射只此一处）。
 *
 * 校验失败返回 `{ ok:false, error }`（面向用户中文；占用 DOSIA 浏览器时另带 `code`，见
 * {@link ConnectorEntryErrorCode}）；成功返回 `{ ok:true, entry }`。
 * 不做档位开关判定（那是 resolveConnectorTiers + 调用方的职责）。凭证明文存 entry
 * env/headers（与现状一致；safeStorage 加密为后续项，见 PR body）。
 */
export function buildConnectorEntry(input) {
    const name = (input.name ?? "").trim();
    if (!name)
        return { ok: false, error: "连接器名称必填。" };
    if (!isValidConnectorName(name))
        return { ok: false, error: "连接器名称不能含空格或「/」。" };
    // B-273：名字先于其它字段判，占了 DOSIA 浏览器的名字就不必再让员工补 URL / 命令。
    if (isReservedBrowserMcpEntry(name, undefined))
        return reservedBrowser();
    const description = input.description?.trim() || undefined;
    const headers = input.headers && Object.keys(input.headers).length > 0 ? input.headers : undefined;
    const env = input.env && Object.keys(input.env).length > 0 ? input.env : undefined;
    if (input.tier === "remote") {
        const url = (input.url ?? "").trim();
        if (!url)
            return { ok: false, error: "远程连接器的 URL 必填。" };
        if (!/^https?:\/\//i.test(url))
            return { ok: false, error: "URL 必须以 http:// 或 https:// 开头。" };
        const type = input.transport === "sse" ? "sse" : "http";
        return { ok: true, entry: { name, type, enabled: true, url, headers, description } };
    }
    if (input.tier === "npm") {
        const pkg = (input.package ?? "").trim();
        if (!pkg)
            return { ok: false, error: "npm 包名必填。" };
        if (/\s/.test(pkg))
            return { ok: false, error: "npm 包名不能含空格。" };
        return stdioEntry({ name, type: "stdio", enabled: true, command: "npx", args: ["-y", pkg], env, description });
    }
    // command 档
    const command = (input.command ?? "").trim();
    if (!command)
        return { ok: false, error: "命令必填。" };
    const args = (input.args ?? []).map((a) => a.trim()).filter(Boolean);
    return stdioEntry({ name, type: "stdio", enabled: true, command, args: args.length ? args : undefined, env, description });
}
/** 本地命令型（npm / command 档）组装完再判一次：换了名字、跑的仍是 chrome-devtools-mcp（B-273）。 */
function stdioEntry(entry) {
    return isReservedBrowserMcpEntry(entry.name, entry) ? reservedBrowser() : { ok: true, entry };
}
//# sourceMappingURL=enterprise-admin.js.map